import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trash2, Plus, Minus } from "lucide-react";
import PhoneVerify from "@/components/PhoneVerify";
import { beginCheckout } from "@/lib/analytics";

function Labeled({ label, required, hint, hintClass = "", children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {label}{required && <span className="text-[hsl(var(--accent))]"> *</span>}
        </p>
        {hint && <span className={`font-mono text-[10px] uppercase tracking-widest ${hintClass}`}>{hint}</span>}
      </div>
      {children}
    </div>
  );
}

export default function Cart() {
  const { items, subtotal, remove, updateQty, refresh, mergeGuest } = useCart();
  const { user, guestSession } = useAuth();
  const nav = useNavigate();
  // The buyer's remembered, previously-verified default mobile (10-digit).
  const defaultPhone = user?.phone_verified && user?.phone ? user.phone : "";
  const [addr, setAddr] = useState({
    full_name: user?.name || "", phone: defaultPhone, line1: "", line2: "",
    city: "", state: "", pincode: "",
  });
  const [step, setStep] = useState(1);
  const [placing, setPlacing] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(!!defaultPhone);
  const [pinStatus, setPinStatus] = useState(null); // null | "loading" | "valid" | "invalid"
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [changingPhone, setChangingPhone] = useState(false);
  const [saveForNext, setSaveForNext] = useState(true);

  // When a guest verifies their mobile at checkout, sign them into (or create) that
  // account straight away. If they're an existing customer, this makes the effect
  // below load their saved addresses so they can pick one before paying (#3).
  const handleVerified = async (ok) => {
    setPhoneVerified(ok);
    if (ok && !user && addr.phone) {
      try { await guestSession(addr.phone, addr.full_name); }
      catch { /* non-fatal — they can still enter a new address and pay */ }
    }
  };

  // Apply a saved address to the form (keeps the verified mobile as-is).
  const applyAddress = (a) => {
    setAddr((prev) => ({
      ...prev,
      full_name: a.full_name || prev.full_name,
      line1: a.line1 || "", line2: a.line2 || "",
      city: a.city || "", state: a.state || "", pincode: a.pincode || "",
    }));
    if ((a.pincode || "").length === 6) setPinStatus("valid");
    setSaveForNext(false); // already saved
  };

  // Save the entered address for next time (after an order is created).
  const maybeSaveAddress = async () => {
    if (!saveForNext) return;
    try { await api.post("/addresses", { ...addr, label: "Home", is_default: savedAddresses.length === 0 }); } catch { /* non-fatal */ }
  };

  useEffect(() => {
    if (!user) return;
    // If the user object arrived after mount, apply their remembered verified mobile.
    if (user.phone_verified && user.phone) {
      setAddr((a) => ({ ...a, phone: a.phone || user.phone }));
      setPhoneVerified((v) => v || true);
    }
    api.get("/addresses").then(({ data }) => {
      setSavedAddresses(data || []);
      const def = (data || []).find((a) => a.is_default) || (data || [])[0];
      if (def) applyAddress(def);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Prices are GST-inclusive — the listed price is what the customer pays.
  const gstIncl = subtotal - subtotal / 1.18; // GST already contained in subtotal
  const shipping = subtotal >= 2000 || subtotal === 0 ? 0 : 99;
  const total = subtotal + shipping;

  // Auto-fill city/state from an Indian PIN code (India Post API).
  const lookupPincode = async (pin) => {
    setPinStatus("loading");
    try {
      const r = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
      const data = await r.json();
      const po = data?.[0]?.Status === "Success" ? data[0].PostOffice?.[0] : null;
      if (po) {
        const city = (po.Block && po.Block !== "NA" ? po.Block : po.District) || "";
        setAddr((a) => ({ ...a, city, state: po.State || "" }));
        setPinStatus("valid");
      } else {
        setPinStatus("invalid");
      }
    } catch {
      setPinStatus("invalid");
    }
  };

  const onPincode = (v) => {
    const pin = v.replace(/\D/g, "").slice(0, 6);
    setAddr((a) => ({ ...a, pincode: pin }));
    if (pin.length === 6) lookupPincode(pin);
    else setPinStatus(null);
  };

  const addressOk =
    addr.full_name.trim() && phoneVerified && addr.pincode.length === 6 &&
    addr.city.trim() && addr.state.trim() && addr.line1.trim();

  const checkout = async () => {
    if (!phoneVerified) { toast.error("Verify your mobile number to continue"); return; }
    setPlacing(true);
    try {
      // Guest checkout: the verified mobile becomes (or logs into) a buyer account,
      // then the local cart is merged in — so the order is a normal buyer order.
      if (!user) {
        await guestSession(addr.phone, addr.full_name);
        await mergeGuest();
      }
      const { data } = await api.post("/orders/checkout", { address: addr });
      const order = data.order;
      await maybeSaveAddress();
      if (data.razorpay_order_id && data.razorpay_key_id && window.Razorpay) {
        const rz = new window.Razorpay({
          key: data.razorpay_key_id,
          amount: data.amount_paise,
          currency: "INR",
          order_id: data.razorpay_order_id,
          name: "TheSpotters",
          description: `Order ${order.id.slice(0, 8)}`,
          prefill: { name: addr.full_name || user?.name, email: user?.email || "", contact: addr.phone },
          theme: { color: "#a13d1e" },
          handler: async (res) => {
            try {
              await api.post("/orders/verify", {
                order_id: order.id,
                razorpay_payment_id: res.razorpay_payment_id,
                razorpay_order_id: res.razorpay_order_id,
                razorpay_signature: res.razorpay_signature,
              });
              await refresh();
              toast.success("Payment received — order placed");
              nav(`/order-confirmed/${order.id}`);
            } catch (e) {
              toast.error(e.response?.data?.detail || "Payment verification failed. You can finish it from Orders → Complete Payment.");
            }
          },
          modal: {
            ondismiss: () => toast("Payment cancelled — finish it anytime from Orders → Complete Payment."),
          },
        });
        rz.on("payment.failed", () => toast.error("Payment failed. You can retry from Orders → Complete Payment."));
        rz.open();
      } else {
        await api.post(`/orders/${order.id}/mock-pay`);
        await refresh();
        toast.success("Order placed (mock payment)");
        nav(`/order-confirmed/${order.id}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
    } finally { setPlacing(false); }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-24 min-h-[60vh]">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Bag</p>
        <h1 className="font-serif text-6xl md:text-7xl mt-2 tracking-tighter">Nothing here yet.</h1>
        <Link to="/shop" data-testid="empty-cart-shop" className="inline-block mt-8 border-b border-foreground pb-1 font-mono text-xs uppercase tracking-widest hover:text-[hsl(var(--accent))]">
          Wander the shop →
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-[1600px] mx-auto px-6 md:px-12 py-16 grid md:grid-cols-12 gap-12">
      <div className="md:col-span-7">
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">The Bag · {items.length} items</p>
        <h1 className="font-serif text-6xl mb-10 tracking-tighter">Your pick.</h1>

        {items.map((it) => (
          <div key={it.id} className="grid grid-cols-[120px_1fr_auto] gap-6 py-5 border-b border-foreground/20 items-center">
            <div className="aspect-square bg-secondary overflow-hidden">
              {it.product?.images?.[0] && <img src={it.product.images[0]} alt="" className="w-full h-full object-cover" />}
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{it.product?.vendor_name}</p>
              <h3 className="font-serif text-xl">{it.product?.title}</h3>
              <p className="font-mono text-xs mt-1 text-muted-foreground">Size {it.size}</p>
              <div className="flex items-center gap-4 mt-3">
                <div className="inline-flex items-center border border-foreground/30">
                  <button data-testid={`qty-dec-${it.id}`} onClick={() => updateQty(it.id, it.quantity - 1)}
                    disabled={it.quantity <= 1} aria-label="decrease quantity"
                    className="w-8 h-8 flex items-center justify-center hover:bg-foreground hover:text-background disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground">
                    <Minus size={13} />
                  </button>
                  <span data-testid={`qty-${it.id}`} className="w-9 text-center font-mono text-sm tabular-nums">{it.quantity}</span>
                  <button data-testid={`qty-inc-${it.id}`} onClick={() => updateQty(it.id, it.quantity + 1)}
                    disabled={it.quantity >= 20} aria-label="increase quantity"
                    className="w-8 h-8 flex items-center justify-center hover:bg-foreground hover:text-background disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-foreground">
                    <Plus size={13} />
                  </button>
                </div>
                <p className="font-mono text-sm">₹ {(it.product?.price * it.quantity).toLocaleString("en-IN")}</p>
              </div>
            </div>
            <button data-testid={`remove-${it.id}`} onClick={() => remove(it.id)} aria-label="remove" className="hover:text-destructive self-start">
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>

      <div className="md:col-span-5">
        <div className="border border-foreground p-8 tag-punch pl-12 sticky top-8">
          <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Order Summary</p>
          <h2 className="font-serif text-4xl mt-2 mb-8">Total.</h2>

          <div className="space-y-2 font-mono text-sm mb-6">
            <div className="flex justify-between"><span>Item total</span><span>₹ {subtotal.toLocaleString("en-IN")}</span></div>
            <div className="flex justify-between text-muted-foreground"><span>incl. GST (18%)</span><span>₹ {gstIncl.toFixed(2)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "FREE" : `₹ ${shipping}`}</span></div>
          </div>
          <div className="border-t border-foreground pt-4 flex justify-between text-xl font-mono font-semibold">
            <span>TOTAL</span><span>₹ {total.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>
          </div>

          {step === 1 && (
            <button data-testid="proceed-address" onClick={() => { beginCheckout(items, total); setStep(2); }}
              className="w-full mt-8 bg-foreground text-background py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-[hsl(var(--accent))]">
              Add Shipping Address
            </button>
          )}
          {step === 2 && (
            <div className="mt-8 space-y-5">
              {savedAddresses.length > 0 && (
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Deliver to a saved address</p>
                  <div className="grid gap-2">
                    {savedAddresses.map((a) => {
                      const active = addr.line1 === a.line1 && addr.pincode === a.pincode;
                      return (
                        <button key={a.id} type="button" onClick={() => applyAddress(a)}
                          className={`text-left border p-3 transition-colors ${active ? "border-foreground bg-foreground/[0.03]" : "border-foreground/25 hover:border-foreground"}`}>
                          <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{a.label || "Address"}{a.is_default ? " · Default" : ""}</span>
                          <p className="text-sm mt-0.5 leading-snug">{a.full_name} — {a.line1}, {a.city}, {a.state} {a.pincode}</p>
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Or enter a new address</p>
                </div>
              )}

              <Labeled label="Full Name" required>
                <input data-testid="addr-full_name" value={addr.full_name}
                  onChange={(e) => setAddr({ ...addr, full_name: e.target.value })}
                  className="underline-input" />
              </Labeled>

              <Labeled label="Mobile Number" required>
                {defaultPhone && !changingPhone && addr.phone === defaultPhone ? (
                  <div className="flex items-center justify-between border-b border-foreground py-2.5">
                    <span className="font-mono text-sm">+91 {defaultPhone} <span className="text-[hsl(var(--accent))] text-xs ml-1">✓ Verified</span></span>
                    <button type="button" onClick={() => { setChangingPhone(true); setPhoneVerified(false); setAddr((a) => ({ ...a, phone: "" })); }}
                      className="font-mono text-[10px] uppercase tracking-widest underline hover:text-[hsl(var(--accent))]">Change</button>
                  </div>
                ) : (
                  <>
                    <PhoneVerify
                      phone={addr.phone}
                      onPhoneChange={(v) => setAddr({ ...addr, phone: v })}
                      onVerified={handleVerified}
                      required
                    />
                    {defaultPhone && (
                      <button type="button" onClick={() => { setChangingPhone(false); setAddr((a) => ({ ...a, phone: defaultPhone })); setPhoneVerified(true); }}
                        className="mt-2 font-mono text-[10px] uppercase tracking-widest underline hover:text-[hsl(var(--accent))]">Use my saved number (+91 {defaultPhone})</button>
                    )}
                  </>
                )}
              </Labeled>

              <Labeled label="PIN Code" required
                hint={pinStatus === "valid" ? "✓ Valid" : pinStatus === "invalid" ? "Not found" : pinStatus === "loading" ? "Checking…" : ""}
                hintClass={pinStatus === "valid" ? "text-[hsl(var(--accent))]" : pinStatus === "invalid" ? "text-destructive" : "text-muted-foreground"}>
                <input data-testid="addr-pincode" value={addr.pincode} inputMode="numeric"
                  placeholder="6-digit PIN code"
                  onChange={(e) => onPincode(e.target.value)}
                  className="underline-input" />
              </Labeled>

              <div className="grid grid-cols-2 gap-4">
                <Labeled label="City" required>
                  <input data-testid="addr-city" value={addr.city}
                    onChange={(e) => setAddr({ ...addr, city: e.target.value })}
                    className="underline-input" />
                </Labeled>
                <Labeled label="State" required>
                  <input data-testid="addr-state" value={addr.state}
                    onChange={(e) => setAddr({ ...addr, state: e.target.value })}
                    className="underline-input" />
                </Labeled>
              </div>

              <Labeled label="Address Line 1" required>
                <input data-testid="addr-line1" value={addr.line1}
                  placeholder="House/Flat No., Building, Street"
                  onChange={(e) => setAddr({ ...addr, line1: e.target.value })}
                  className="underline-input" />
              </Labeled>

              <Labeled label="Address Line 2">
                <input data-testid="addr-line2" value={addr.line2}
                  placeholder="Area, Landmark (optional)"
                  onChange={(e) => setAddr({ ...addr, line2: e.target.value })}
                  className="underline-input" />
              </Labeled>

              <label className="flex items-center gap-2.5 font-mono text-[11px] cursor-pointer select-none">
                <input type="checkbox" checked={saveForNext} onChange={(e) => setSaveForNext(e.target.checked)}
                  className="w-4 h-4 accent-[hsl(var(--accent))]" />
                Save this address for next time
              </label>

              <button data-testid="place-order-btn" onClick={checkout} disabled={placing || !addressOk}
                className="w-full mt-2 bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] py-4 font-mono text-xs uppercase tracking-[0.24em] hover:bg-foreground disabled:opacity-50">
                {placing ? "Processing..."
                  : !phoneVerified ? "Verify mobile to pay"
                  : !addressOk ? "Complete address to pay"
                  : "Pay with Razorpay"}
              </button>
              <p className="font-mono text-[10px] text-muted-foreground text-center">
                Test mode: order will place via mock payment if Razorpay keys aren't configured.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
